import { useEffect, useState, useCallback, useRef } from "react";
import api, { qs, apiError } from "@/lib/api";
import { fmtINR, fmtDay } from "@/lib/service";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, RefreshCw, Upload, Search } from "lucide-react";

const TH = "px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-muted-foreground whitespace-nowrap";
const CLS_TONE = { regular: "bg-secondary", ccs: "bg-violet-100 text-violet-900", accs: "bg-sky-100 text-sky-900", unclassified: "bg-amber-100 text-amber-900" };
const ClsBadge = ({ cls, label }) => <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${CLS_TONE[cls] || "bg-secondary"}`}>{label || cls}</span>;

function Queue({ meta, onChanged }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/service/codes/queue").then(({ data }) => setRows(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const assign = async (r, cls) => {
    try {
      const { data } = await api.put("/service/codes", { kind: r.kind, code: r.code, cls, desc: r.desc });
      toast.success(`${r.code} → ${cls} · ${data.lines_updated} lines re-tagged`);
      load(); onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="border bg-card rounded-md overflow-auto" data-testid="svc-queue">
      <div className="px-4 py-3 border-b flex items-center gap-3"><h3 className="font-heading text-sm font-semibold">Unclassified queue</h3><span className="text-[11px] text-muted-foreground">{rows.length} codes · ₹{fmtINR(rows.reduce((a, r) => a + r.revenue, 0))} counted as regular until assigned</span></div>
      <table className="w-full text-xs">
        <thead className="border-b"><tr>{["Kind", "Code", "Description", "Lines", "Revenue ₹", "Workshops", "Seen", "Assign"].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.kind}-${r.code}`} className="border-b last:border-0" data-testid={`svc-queue-row-${r.code}`}>
              <td className="px-3 py-1.5 text-muted-foreground">{r.kind}</td>
              <td className="px-3 py-1.5 font-mono font-semibold">{r.code}</td>
              <td className="px-3 py-1.5">{r.desc}</td>
              <td className="px-3 py-1.5 num">{r.lines}</td>
              <td className="px-3 py-1.5 num font-semibold">{fmtINR(r.revenue)}</td>
              <td className="px-3 py-1.5 text-muted-foreground">{r.dealers.join(", ")}</td>
              <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap">{fmtDay(r.first_seen)} → {fmtDay(r.last_seen)}</td>
              <td className="px-3 py-1.5"><div className="flex gap-1">{meta.classes.map((c) => <button key={c.key} onClick={() => assign(r, c.key)} className={`rounded border px-2 py-0.5 text-[10px] font-semibold hover:opacity-80 ${CLS_TONE[c.key]}`} data-testid={`svc-queue-assign-${r.code}-${c.key}`}>{c.label}</button>)}</div></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={8} className="px-3 py-5 text-center text-muted-foreground">Queue is empty — every code on file is classified.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Rules({ meta, onChanged }) {
  const [rules, setRules] = useState([]);
  const [draft, setDraft] = useState({ kind: "part", op: "prefix", pattern: "", cls: "accs", priority: 40, note: "" });
  const load = useCallback(() => api.get("/service/rules").then(({ data }) => setRules(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    try { await api.post("/service/rules", { ...draft, priority: Number(draft.priority) }); toast.success("Rule added — run re-classification to apply to stored lines"); setDraft({ ...draft, pattern: "", note: "" }); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (r, active) => { try { await api.put(`/service/rules/${r.id}`, { active }); load(); } catch (e) { toast.error(apiError(e)); } };
  const remove = async (r) => { if (!window.confirm(`Delete rule ${r.op} "${r.pattern}" → ${r.cls}?`)) return; try { await api.delete(`/service/rules/${r.id}`); load(); onChanged(); } catch (e) { toast.error(apiError(e)); } };
  const Sel = ({ value, onChange, options, testId, w = "w-28" }) => (
    <Select value={value} onValueChange={onChange}><SelectTrigger className={`h-8 ${w} text-xs`} data-testid={testId}><SelectValue /></SelectTrigger><SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select>
  );
  return (
    <div className="border bg-card rounded-md overflow-auto" data-testid="svc-rules">
      <div className="px-4 py-3 border-b"><h3 className="font-heading text-sm font-semibold">Classification rules</h3><p className="text-xs text-muted-foreground">Precedence: manual assignment › code master › rules (lowest priority number first) › unclassified. Codes on the master list ignore rules.</p></div>
      <table className="w-full min-w-[980px] text-xs">
        <thead className="border-b"><tr>{["Kind", "Match", "Pattern", "Class", "Priority", "Note", "Active", ""].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
        <tbody>
          {rules.map((r) => (
            <tr key={r.id} className={`border-b ${r.active ? "" : "opacity-50"}`} data-testid={`svc-rule-row-${r.id}`}>
              <td className="px-3 py-1.5 whitespace-nowrap">{r.kind}</td>
              <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap">{r.op}</td>
              <td className="px-3 py-1.5 font-mono whitespace-nowrap">{r.pattern}</td>
              <td className="px-3 py-1.5 whitespace-nowrap"><ClsBadge cls={r.cls} label={meta.classes.find((c) => c.key === r.cls)?.label} /></td>
              <td className="px-3 py-1.5 num">{r.priority}</td>
              <td className="px-3 py-1.5 text-muted-foreground min-w-[220px]">{r.note}</td>
              <td className="px-3 py-1.5"><Switch checked={r.active} onCheckedChange={(v) => toggle(r, v)} data-testid={`svc-rule-active-${r.id}`} /></td>
              <td className="px-3 py-1.5"><button onClick={() => remove(r)} className="text-muted-foreground hover:text-red-600" data-testid={`svc-rule-delete-${r.id}`}><Trash2 size={13} /></button></td>
            </tr>
          ))}
          <tr className="bg-secondary/30" data-testid="svc-rule-draft">
            <td className="px-3 py-2"><Sel value={draft.kind} onChange={(v) => setDraft({ ...draft, kind: v })} options={[{ value: "part", label: "part" }, { value: "labour", label: "labour" }]} testId="svc-rule-kind" w="w-24" /></td>
            <td className="px-3 py-2"><Sel value={draft.op} onChange={(v) => setDraft({ ...draft, op: v })} options={meta.ops.map((o) => ({ value: o, label: o }))} testId="svc-rule-op" w="w-28" /></td>
            <td className="px-3 py-2"><Input value={draft.pattern} onChange={(e) => setDraft({ ...draft, pattern: e.target.value })} placeholder="e.g. ACF55" className="h-8 text-xs font-mono w-40" data-testid="svc-rule-pattern" /></td>
            <td className="px-3 py-2"><Sel value={draft.cls} onChange={(v) => setDraft({ ...draft, cls: v })} options={meta.classes.map((c) => ({ value: c.key, label: c.label }))} testId="svc-rule-cls" w="w-36" /></td>
            <td className="px-3 py-2"><Input type="number" value={draft.priority} onChange={(e) => setDraft({ ...draft, priority: e.target.value })} className="h-8 text-xs w-16" data-testid="svc-rule-priority" /></td>
            <td className="px-3 py-2"><Input value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} placeholder="why" className="h-8 text-xs" data-testid="svc-rule-note" /></td>
            <td className="px-3 py-2" colSpan={2}><Button size="sm" className="h-8" onClick={add} disabled={!draft.pattern.trim()} data-testid="svc-rule-add">Add rule</Button></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function CodeLookup({ meta, onChanged }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const search = useCallback(() => { if (!q.trim()) return setRes(null); api.get(`/service/codes?${qs({ q, limit: 50 })}`).then(({ data }) => setRes(data)).catch(() => {}); }, [q]);
  useEffect(() => { const t = setTimeout(search, 300); return () => clearTimeout(t); }, [search]);
  const setCls = async (r, cls) => { try { await api.put("/service/codes", { kind: r.kind, code: r.code, cls, desc: r.desc }); toast.success(`${r.code} → ${cls}`); search(); onChanged(); } catch (e) { toast.error(apiError(e)); } };
  const remove = async (r) => { try { const { data } = await api.delete(`/service/codes/${r.kind}/${encodeURIComponent(r.code)}`); toast.success(`${r.code} removed from master — now ${data.now}`); search(); onChanged(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="border bg-card rounded-md" data-testid="svc-code-lookup">
      <div className="px-4 py-3 border-b flex flex-wrap items-center gap-3">
        <h3 className="font-heading text-sm font-semibold">Code master lookup</h3>
        <span className="text-[11px] text-muted-foreground">{meta.codes.master} master · {meta.codes.manual} manual</span>
        <div className="relative ml-auto"><Search size={13} className="absolute left-2 top-2.5 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="part no, labour code or description" className="h-8 pl-7 text-xs w-72" data-testid="svc-code-search" /></div>
      </div>
      {res && (
        <table className="w-full text-xs">
          <thead className="border-b"><tr>{["Kind", "Code", "Description", "Class", "Source", ""].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
          <tbody>
            {res.rows.map((r) => (
              <tr key={r.id} className="border-b last:border-0" data-testid={`svc-code-row-${r.code}`}>
                <td className="px-3 py-1.5 text-muted-foreground">{r.kind}</td>
                <td className="px-3 py-1.5 font-mono font-semibold">{r.code}</td>
                <td className="px-3 py-1.5">{r.desc}</td>
                <td className="px-3 py-1.5">
                  <Select value={r.cls} onValueChange={(v) => setCls(r, v)}><SelectTrigger className="h-7 w-36 text-xs" data-testid={`svc-code-cls-${r.code}`}><SelectValue /></SelectTrigger><SelectContent>{meta.classes.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent></Select>
                </td>
                <td className="px-3 py-1.5 text-muted-foreground">{r.source}{r.updated_by && r.source === "manual" ? ` · ${r.updated_by}` : ""}</td>
                <td className="px-3 py-1.5 text-right"><button onClick={() => remove(r)} className="text-muted-foreground hover:text-red-600" title="Remove from master (fall back to rules)" data-testid={`svc-code-remove-${r.code}`}><Trash2 size={13} /></button></td>
              </tr>
            ))}
            {res.rows.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-center text-muted-foreground">No master entry matches — the code is classified by rules (or sits in the queue)</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function ClassificationAdmin() {
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const fileRef = useRef(null);
  const loadMeta = useCallback(() => api.get("/service/classification/meta").then(({ data }) => setMeta(data)).catch(() => {}), []);
  useEffect(() => { loadMeta(); }, [loadMeta, tick]);
  const changed = () => setTick((t) => t + 1);

  const reclassify = async () => {
    setBusy(true);
    try { const { data } = await api.post("/service/reclassify"); toast.success(`Re-classified — ${data.lines_changed} lines changed`); changed(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const importMaster = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/service/codes/import", fd);
      toast.success(`${data.codes_written} codes written (${data.by_class.accs} ACCS · ${data.by_class.ccs} CCS) · ${data.lines_changed} lines re-tagged`);
      changed();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!meta) return null;
  return (
    <div className="space-y-4" data-testid="svc-classification">
      <div className="border bg-card rounded-md p-4 flex flex-wrap items-center gap-3">
        <div>
          <h3 className="font-heading text-sm font-semibold">Revenue classification</h3>
          <p className="text-xs text-muted-foreground">RO category comes from the DMS work type ({meta.ro_cats.map((c) => `${c.work_type} → ${c.cat}`).join(" · ")}). Every labour / part line is tagged Regular, CCS (car care) or Accessories.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => { importMaster(e.target.files?.[0]); e.target.value = ""; }} data-testid="svc-master-file" />
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="svc-master-import"><Upload size={13} className="mr-1" /> Import code master</Button>
          <Button variant="outline" size="sm" onClick={reclassify} disabled={busy} data-testid="svc-reclassify"><RefreshCw size={13} className={`mr-1 ${busy ? "animate-spin" : ""}`} /> Re-run classification</Button>
        </div>
      </div>
      <Queue key={`q${tick}`} meta={meta} onChanged={changed} />
      <Rules meta={meta} onChanged={changed} />
      <CodeLookup key={`c${tick}`} meta={meta} onChanged={changed} />
    </div>
  );
}
